import React from "react";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { Button } from "primereact/button";
import { Link } from "react-router-dom";
import localeText from "../../../../locale";
import { cloudinaryInstance } from "../../../../utilities/Cloudinary/cloudinaryInstance";
import { formatDateTime } from "../../../../utilities/formatDate";
import { formatPrice } from "../../../../utilities/formatPrice";
import {
  buildTimelineEvents,
  getStatusBadgeKey,
  formatAddress,
  isPositiveAmount,
} from "./orderDetailsUtils";
import OrderDetailsSellerReceivedCard from "./OrderDetailsSellerReceivedCard";

const OrderDetailsMainGrid = ({ currentOrder, currencySymbol, links, sales, isSeller }) => {
  if (!currentOrder) return null;

  const items = currentOrder.itens || [];
  const status = currentOrder.status;
  const showSellerReceived = Boolean(sales && isSeller);

  const hasAnyPaymentAmount =
    isPositiveAmount(currentOrder.amountWalletExtracoin) ||
    isPositiveAmount(currentOrder.amountWallet) ||
    isPositiveAmount(currentOrder.amountCash) ||
    isPositiveAmount(currentOrder.amountTransferBank);

  const metaAmountSuffix = isPositiveAmount(currentOrder.amountTotal)
    ? ` | ${currencySymbol} ${formatPrice(currentOrder.amountTotal)}`
    : "";

  return (
    <div className="order-details-grid">
      <div className="flex flex-column gap-4">
        <div className="order-details-card">
          <div className="order-details-transaction-header">
            <div>
              <div className="order-details-transaction-id">
                {localeText("orderDetailsTransactionId")} #{currentOrder.id}
              </div>
              <div className="order-details-transaction-meta">
                {currentOrder.date && formatDateTime(currentOrder.date)}
                {metaAmountSuffix}
              </div>
            </div>
            <span
              className={`order-details-status-badge order-details-status-badge--${
                status === "PAID" ? "process" : status === "SENT" ? "shipped" : "delivered"
              }`}
            >
              {localeText(getStatusBadgeKey(status))}
            </span>
          </div>

          <div className="order-details-section-title">
            {localeText("orderDetailsSummaryPurchase")}
          </div>

          {items.map((item) => {
            const cldImg =
              item.productVariation?.imageid &&
              cloudinaryInstance
                .image(item.productVariation.imageid)
                .resize(fill().width(96).height(96))
                .format("webp");

            const fallbackImg =
              !cldImg &&
              item.product?.imageid1 &&
              cloudinaryInstance
                .image(item.product.imageid1)
                .resize(fill().width(96).height(96))
                .format("webp");

            const price =
              item.product?.promotionalPriceUnique && !item.productVariation
                ? item.product.promotionalPriceUnique
                : item.priceUnitary;

            const downloadable = currentOrder?.itens?.[0]?.product?.downloadable;

            return (
              <div key={`${item.product?.id}-${item.quantity}`} className="order-details-item-row">
                {(cldImg || fallbackImg) && (
                  <AdvancedImage
                    cldImg={cldImg || fallbackImg}
                    className="order-details-item-thumb"
                    style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                  />
                )}
                <div className="order-details-item-info">
                  <div className="order-details-item-name">
                    <Link to={`/product/${item.product?.id}`}>{item.product?.title}</Link>
                  </div>
                  <div className="order-details-item-qty-price">
                    {item.quantity}x{" "}
                    {isPositiveAmount(price) ? `${currencySymbol} ${formatPrice(price)}` : "—"}
                  </div>
                </div>
                {/* {!downloadable && (
                  <button
                    type="button"
                    className="p-button p-button-text p-button-rounded"
                    aria-label="Options"
                  >
                    <i className="pi pi-ellipsis-v" />
                  </button>
                )} */}
                {downloadable && status === "RECEIVED" && links.length > 0 && (
                  <a
                    href={links.find((l) => l.id === item.product?.id)?.urldownload}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button
                      icon="pi pi-download"
                      className="p-button-text p-button-sm p-no-outline"
                    />
                  </a>
                )}
              </div>
            );
          })}

          {isPositiveAmount(currentOrder.amountOrder) && (
            <div className="order-details-summary-row">
              <span>{localeText("orderDetailsSubtotal") || localeText("subtotal")}</span>
              <span>
                {currencySymbol} {formatPrice(currentOrder.amountOrder)}
              </span>
            </div>
          )}
          {isPositiveAmount(currentOrder.amountToPlatform) && (
            <div className="order-details-summary-row">
              <span>{localeText("platformFee")}</span>
              <span>
                {currencySymbol} {formatPrice(currentOrder.amountToPlatform)}
              </span>
            </div>
          )}
          {isPositiveAmount(currentOrder?.amountShipment) && (
            <div className="order-details-summary-row">
              <span>{localeText("orderDetailsShippingCosts") || localeText("freight")}</span>
              <span>
                {currencySymbol} {formatPrice(currentOrder.amountShipment)}
              </span>
            </div>
          )}
          {!showSellerReceived && isPositiveAmount(currentOrder?.cashback) && (
            <div className="order-details-summary-row">
              <span>{localeText("orderDetailsCashback")}</span>
              <span>
                {currencySymbol} {formatPrice(currentOrder.cashback)}
              </span>
            </div>
          )}
          {isPositiveAmount(currentOrder.amountTotal) && (
            <div className="order-details-summary-row order-details-summary-row--total">
              <span>{localeText("orderDetailsTotal") || localeText("total")}</span>
              <span>
                {currencySymbol} {formatPrice(currentOrder.amountTotal)}
              </span>
            </div>
          )}

          {status !== "PENDING" && hasAnyPaymentAmount && (
            <>
              <div className="order-details-section-title">
                {localeText("orderDetailsPaymentMethod") || localeText("howWasPaid")}
              </div>
              {isPositiveAmount(currentOrder.amountWalletExtracoin) && (
                <div className="order-details-summary-row">
                  <span>{localeText("orderDetailsExtracredit")}</span>
                  <span>
                    {currencySymbol} {formatPrice(currentOrder.amountWalletExtracoin)}
                  </span>
                </div>
              )}
              {isPositiveAmount(currentOrder.amountWallet) && (
                <div className="order-details-summary-row">
                  <span>{localeText("amountWallet")}</span>
                  <span>
                    {currencySymbol} {formatPrice(currentOrder.amountWallet)}
                  </span>
                </div>
              )}
              {isPositiveAmount(currentOrder.amountCash) && (
                <div className="order-details-summary-row">
                  <span>{localeText("amountCash")}</span>
                  <span>
                    {currencySymbol} {formatPrice(currentOrder.amountCash)}
                  </span>
                </div>
              )}
              {isPositiveAmount(currentOrder.amountTransferBank) && (
                <div className="order-details-summary-row">
                  <span>{localeText("paymentMethodBankTransfer")}</span>
                  <span>
                    {currencySymbol} {formatPrice(currentOrder.amountTransferBank)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
        {showSellerReceived && (
          <OrderDetailsSellerReceivedCard
            currentOrder={currentOrder}
            currencySymbol={currencySymbol}
          />
        )}
      </div>

      <div className="flex flex-column gap-4">
        <div className="order-details-card">
          <div className="order-details-transaction-header">
            <div>
              <div className="order-details-transaction-id">
                {localeText("orderDetailsTransactionId")} #{currentOrder.id}
              </div>
              <div className="order-details-transaction-meta">
                {currentOrder.date && formatDateTime(currentOrder.date)}
                {metaAmountSuffix}
              </div>
            </div>
          </div>

          <div className="order-details-section-title">
            {localeText("orderDetailsSummaryPurchase")}
          </div>
          {buildTimelineEvents(currentOrder).map((ev) => (
            <div key={ev.key} className="flex align-items-center gap-3 mb-3">
              <div
                className="w-2rem h-2rem border-round flex align-items-center justify-content-center"
                style={{
                  border: `1px solid var(--theme-border)`,
                  color: "var(--theme-text-muted)",
                }}
              >
                <i className={ev.icon} />
              </div>
              <div className="order-details-timeline-body">
                <div className="order-details-timeline-title">{localeText(ev.titleKey)}</div>
                <div className="order-details-timeline-desc">{localeText(ev.descKey)}</div>
              </div>
              <div className="order-details-timeline-date">
                {ev.date ? formatDateTime(ev.date) : ""}
              </div>
            </div>
          ))}
        </div>
        <div className="order-details-card pt-0">
          <div className="order-details-section-title">{localeText("orderDetailsBuyer")}</div>
          {(() => {
            const person = sales ? currentOrder.buyer : currentOrder.seller;
            const initials =
              [person?.firstname?.[0], person?.surname?.[0]]
                .filter(Boolean)
                .join("")
                .toUpperCase() || "?";
            const phoneMask =
              person?.phone?.length > 6
                ? `${person.phone.slice(0, 3)}.***.${person.phone.slice(-3)}`
                : person?.phone || "";
            return (
              <div className="order-details-buyer-row">
                <div className="order-details-avatar">{initials}</div>
                <div>
                  <div className="order-details-item-name">
                    {person?.firstname} {person?.surname}
                  </div>
                  <div className="order-details-item-qty-price">{phoneMask}</div>
                </div>
              </div>
            );
          })()}

          <div className="order-details-section-title">{localeText("orderDetailsContact")}</div>
          {(() => {
            const person = sales ? currentOrder.buyer : currentOrder.seller;
            return (
              <>
                <div className="order-details-contact-line">
                  <strong>{localeText("orderDetailsPhone")}:</strong> {person?.phone || "—"}
                </div>
                <div className="order-details-contact-line">
                  <strong>{localeText("orderDetailsEmail")}:</strong> {person?.email || "—"}
                </div>
                <div className="order-details-contact-line">
                  <strong>{localeText("orderDetailsShippingData")}:</strong>{" "}
                  {formatAddress(sales ? currentOrder.buyer : currentOrder.seller) || "—"}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsMainGrid;
